import type { ReactNode } from 'react';
import {
  Auth0Context,
  type Auth0ContextInterface,
  type IdToken,
  type User,
} from '@auth0/auth0-react';
import {
  CLAIM_AIRLINE,
  CLAIM_OPERATOR_URN,
  CLAIM_ROLE,
  isSystemRoleKey,
  type SystemRoleKey,
} from '@nexa/auth';
import { AccessOverrideProvider } from '@/lib/access/access-override-provider';
import { accessOfSystemRole } from '@/lib/access/resolve';

// Offline development provider per docs/ui/auth0-frontend-tds.md §14.2.
// Mirrors apps/admin-ui's MockAuth0Provider. Toggled by VITE_AUTH0_DISABLE=true,
// gated to import.meta.env.DEV by AuthProvider.
//
// VITE_DEV_ROLE picks the mock operator's role (default: airport_operator): any of the
// seven SYSTEM roles (users-roles-permissions §4.17 — admin, ops_supervisor,
// airport_operator, counter_agent, finance_audit, config_manager, viewer), or `none` for a
// member with no access assigned ("Sin acceso asignado"). The console's permissions come
// from that system role (SYSTEM_ROLES), tenant-wide; the legacy role claim RoleGate still
// reads is the role's baseTier. The bypass BFF answers `/v1/me` for ITS own operator, so
// its access only overrides this one when it is a real access-store assignment
// (`?dev_user=` against a seeded store).

function pickDevRole(): SystemRoleKey | 'none' {
  const raw = import.meta.env.VITE_DEV_ROLE;
  if (raw === 'none') return 'none';
  if (raw && isSystemRoleKey(raw)) return raw;
  return 'airport_operator';
}

const devRole = pickDevRole();
const devAccess = accessOfSystemRole(devRole === 'none' ? null : devRole, null, 'dev');

const mockUser: User = {
  sub: 'auth0|mock-operator',
  name: 'Local Dev Operator',
  email: 'operator@dev.nexa.studio',
  email_verified: true,
  // The mocked operator belongs to the airline this build is pinned to — otherwise a
  // `VITE_TENANT=sky` dev run would trip the TenantGuard on a latam claim. Falls back to
  // latam when no tenant is pinned (the neutral-theme dev case).
  [CLAIM_AIRLINE]: `urn:airline:${(import.meta.env.VITE_TENANT ?? 'latam').toLowerCase()}`,
  // A role-less member carries no role claim (post-login Action, R3).
  ...(devAccess.legacyRole ? { [CLAIM_ROLE]: devAccess.legacyRole } : {}),
  [CLAIM_OPERATOR_URN]: 'urn:user:auth0|mock-operator',
};

const mockIdTokenClaims: IdToken = {
  __raw: 'dev.local.id-token',
  sub: mockUser.sub,
  ...mockUser,
};

const noopAsync = async (): Promise<void> => undefined;
const notImplemented = () => {
  throw new Error('MockAuth0Provider does not implement this Auth0 SDK method.');
};

const mockValue = {
  isAuthenticated: true,
  isLoading: false,
  user: mockUser,
  getAccessTokenSilently: async () => 'dev.local.token',
  getAccessTokenWithPopup: async () => 'dev.local.token',
  getIdTokenClaims: async () => mockIdTokenClaims,
  loginWithRedirect: noopAsync,
  loginWithPopup: noopAsync,
  logout: noopAsync,
  handleRedirectCallback: async () => ({ appState: undefined }),
  loginWithCustomTokenExchange: notImplemented,
  exchangeToken: notImplemented,
  connectAccountWithRedirect: notImplemented,
  getDpopNonce: () => undefined,
} as unknown as Auth0ContextInterface<User>;

interface MockAuth0ProviderProps {
  children: ReactNode;
}

export function MockAuth0Provider({ children }: MockAuth0ProviderProps) {
  return (
    <Auth0Context.Provider value={mockValue}>
      <AccessOverrideProvider access={devAccess} preferStore>
        {children}
      </AccessOverrideProvider>
    </Auth0Context.Provider>
  );
}
